import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { isNumeric, isEmpty } from '../../js/app/react/validators';
import {
  EmergencyFundBar,
  EducationProgressBar,
} from '../../js/app/react/chartDescriptors.overview';
import { touch_object as touchObject } from '../../js/app/react/helpers.objects';

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

export const propsSelector = (_, props) => props;
export const stateSelector = state => state;

export const clientIdSelector = (state, { clientId }) => {
  if (!isNumeric(clientId)) {
    throw Error('clientId must be a valid numeric value');
  }
  return clientId;
};

export const recommendedTargetAmountChartSelector = createSelector(
  stateSelector,
  clientIdSelector,
  (state, clientId) => get(state, `RecommendedTargetAmountChart.${clientId}`, {}),
);

export const showLoadingSelector = createSelector(
  recommendedTargetAmountChartSelector,
  recommendedTargetAmountChart => !!recommendedTargetAmountChart.showLoading,
);

export const loadingSelector = createSelector(
  recommendedTargetAmountChartSelector,
  ({ loading }) => {
    if (typeof loading === 'undefined') {
      return true;
    }
    return loading;
  },
);

export const nullableResponseDataSelector = createSelector(
  recommendedTargetAmountChartSelector,
  propsSelector,
  ({ responseData: responseDataState }, { responseData: responseDataProps }) => {
    if (responseDataProps) {
      return responseDataProps || null;
    }
    return responseDataState || null;
  },
);

const parseData = data => {
  const goal = data.Chart.Data.Series.Recommended;
  const current = data.Chart.Data.Series.Current;
  return {
    Goal: typeof goal !== 'undefined' ? [goal] : data.Chart.Data.Series.Expenses,
    Current: typeof current !== 'undefined' ? [current] : data.Chart.Data.Series.Covered,
    Categories: data.Chart.Data.Categories,
    DesiredPurchaseYear: [data.DesiredPurchaseYear || data.FundDate],
  };
};

function renderChartLabels() {
  const { ADV } = window;
  const { DesiredPurchaseYear, Current, Goal } = this.options.custom;
  if (this.labelGroup) {
    this.labelGroup.destroy();
  }

  this.labelGroup = this.renderer
    .g()
    .attr({
      zIndex: 10,
      id: 'chart-labels',
    })
    .add();

  const center = this.renderer.height / 2.0;
  const offset = 65;

  const YOFFSETS = [center + offset, center - offset];
  const CURRENT_LABEL_POINT = {
    x: this.yAxis[0].toPixels(Current[0]),
    y: YOFFSETS[0],
  };

  // Prevent overlap
  let textAlign;

  let textX;
  if (CURRENT_LABEL_POINT.x < 100) {
    textAlign = 'left';

    textX = CURRENT_LABEL_POINT.x - 5;
  } else {
    textAlign = 'right';
    textX = CURRENT_LABEL_POINT.x + 3;
  }

  this.renderer
    .text(
      '\uf0d8', // fa-caret-up
      CURRENT_LABEL_POINT.x + 6.25,
      CURRENT_LABEL_POINT.y - 10,
    )
    .attr({ align: 'right' })
    .css({
      fontFamily: '"Font Awesome 5 Pro"',
      fontWeight: '900',
      fontSize: '20px',
      color: '#6c727f',
    })
    .add(this.labelGroup);

  const AMOUNT = `$${ADV.util.number_format(Current[0])}`;
  this.renderer
    .text(
      `AMOUNT SAVED BY ${DesiredPurchaseYear}<br><span style="font-weight:700;font-size:17px;">${AMOUNT}</span>`,
      textX,
      CURRENT_LABEL_POINT.y + 3,
    )
    .attr({
      align: textAlign,
    })
    .css({
      fontSize: '12px',
      fontWeight: '600',
      color: '#6c727f',
      fontFamily: 'Open Sans, helvetica, sans-serif',
      lineHeight: '17px',
    })
    .add(this.labelGroup);

  const RECOMMENDED_LABEL_POINT = {
    x: this.yAxis[0].toPixels(Goal[0]),
    y: YOFFSETS[1],
  };

  if (RECOMMENDED_LABEL_POINT.x < 100) {
    textAlign = 'left';
    textX = RECOMMENDED_LABEL_POINT.x - 5;
  } else {
    textAlign = 'right';
    textX = RECOMMENDED_LABEL_POINT.x + 8;
  }

  this.renderer
    .text(
      '\uf0d7', // fa-caret-down
      RECOMMENDED_LABEL_POINT.x + 6.25,
      RECOMMENDED_LABEL_POINT.y + 10,
    )
    .attr({ align: 'right' })
    .css({
      fontFamily: '"Font Awesome 5 Pro"',
      fontSize: '20px',
      fontWeight: '900',
      color: '#0078D4',
    })
    .add(this.labelGroup);

  const RECOMMENDED_AMOUNT = `$${ADV.util.number_format(Goal[0])}`;
  this.renderer
    .text(
      `GOAL<br><span style="font-weight:700;font-size:18px;">${RECOMMENDED_AMOUNT}</span>`,
      textX,
      RECOMMENDED_LABEL_POINT.y - 29,
    )
    .attr({
      align: textAlign,
    })
    .css({
      fontSize: '13px',
      fontWeight: '600',
      color: '#0078D4',
      fontFamily: 'Open Sans, helvetica, sans-serif',
      lineHeight: '18px',
    })
    .add(this.labelGroup);
}

export const nullableChartDataSelector = createDeepEqualSelector(
  nullableResponseDataSelector,
  propsSelector,
  (nullableResponseData, { isPrint, chartDataPrint }) => {
    let recommendedTargetAmountChartData;

    if (isPrint) {
      recommendedTargetAmountChartData = chartDataPrint;
    } else {
      recommendedTargetAmountChartData = nullableResponseData;
    }

    if (isEmpty(recommendedTargetAmountChartData)) {
      return null;
    }

    const { Goal, Current, Categories, DesiredPurchaseYear } = parseData(
      recommendedTargetAmountChartData.GoalChart,
    );

    const multiple = Categories != null;
    const chartData = multiple ? cloneDeep(EducationProgressBar) : cloneDeep(EmergencyFundBar);
    touchObject(chartData, 'xAxis');
    chartData.accessibility = {
      keyboardNavigation: {
        enabled: false,
      },
    };

    if (isPrint) {
      chartData.series[0].color = '#e5f8ff';
      chartData.series = chartData.series.map(serie => ({ ...serie, animation: false }));
    }

    chartData.xAxis.categories = multiple ? Categories : ['Fund Size'];
    chartData.series[0].data = Goal;
    chartData.series[1].data = Current;

    if (!multiple) {
      chartData.custom = {
        DesiredPurchaseYear,
        Current,
        Goal,
      };
      chartData.chart.events = {
        load: renderChartLabels,
        redraw: renderChartLabels,
      };
    }
    return chartData;
  },
);
